/**
 * Gravity's `StandardDialog` with its type scale made root-independent.
 *
 * Sizes come from `shared/styles/gravity-type`, which covers why they are px and
 * how heavily a selector has to be weighted to survive. Two things are particular
 * to this component.
 *
 * The dialog root carries no class to build that weight from beyond the marker
 * itself, so `DIALOG_SELECTOR` repeats it. `!important` would work too, at the
 * cost of handing the same problem to whoever needs to override these next.
 *
 * And the styles are global because `Dialog` portals to `document.body`, so a
 * styled wrapper in the React tree would not contain the rendered markup. They
 * hang off the class `Dialog` puts on the portaled `.gv-modal` root instead.
 */
import React from 'react';
import { StandardDialog } from '@shore/react-gravity';
import { createGlobalStyle } from 'styled-components';
import { gravityFontSize, gravityFluidFontSize } from '../styles/gravity-type';

const DIALOG_CLASS = 'as-gravity-dialog';

/** The class twice over: same elements matched, one more class of weight. */
const DIALOG_SELECTOR = `.${DIALOG_CLASS}.${DIALOG_CLASS}`;

const HEADING = gravityFluidFontSize(
  '--font-size-heading-lg-min',
  '--font-size-heading-lg-max'
);
const LEAD = gravityFluidFontSize(
  '--font-size-text-lg-min',
  '--font-size-text-lg-max'
);

const GravityDialogStyles = createGlobalStyle`
  .gv-activated .gv-modal${DIALOG_SELECTOR} .gv-modal-title {
    font-size: ${HEADING};
  }

  .gv-activated .gv-modal${DIALOG_SELECTOR} .gv-modal-subtitle {
    font-size: ${LEAD};
  }

  .gv-activated .gv-modal${DIALOG_SELECTOR} .gv-modal-body .gv-product-intro {
    font-size: ${LEAD};
  }

  /* Body copy and buttons take the one non-fluid token, so no ramp. */
  .gv-activated .gv-modal${DIALOG_SELECTOR} .gv-modal-body,
  .gv-activated .gv-modal${DIALOG_SELECTOR} .gv-button {
    font-size: ${gravityFontSize('--font-size-text-sm')};
  }
`;

export interface GravityDialogProps {
  isOpen?: boolean;
  /** Renders Gravity's close glyph, top-right. Omit for a dialog with no X. */
  onClose?: () => void;
  onClickAway?: () => void;
  onEscape?: () => void;
  title?: React.ReactNode;
  /**
   * Rendered as a sibling of the body, inside `.gv-modal-content`. With
   * `modalClassName="gv-upgrade-modal"` that content box is a flex row, so this
   * slot carries whatever sits outside the copy column — pass a fragment for
   * more than one node.
   */
  footer?: React.ReactNode;
  width?: number | string;
  maxWidth?: number | string;
  padding?: number | string;
  /** Extra classes for the portaled `.gv-modal` root, e.g. `gv-upgrade-modal`. */
  modalClassName?: string;
  bodyClassName?: string;
  children?: React.ReactNode;
}

export function GravityDialog({
  modalClassName,
  children,
  ...props
}: GravityDialogProps) {
  return (
    <>
      <GravityDialogStyles />
      <StandardDialog
        {...props}
        classes={{
          modal: modalClassName
            ? `${DIALOG_CLASS} ${modalClassName}`
            : DIALOG_CLASS,
        }}
      >
        {children}
      </StandardDialog>
    </>
  );
}
